import { createClient } from '@supabase/supabase-js';
import { normalizeImageUrl } from '../utils/api';

// Supabase configuration
// These would be moved to environment variables in production
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'YOUR_SUPABASE_URL';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY';

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  db: {
    schema: 'public'
  }
});

// Database helper functions
export const supabaseHelpers = {
  // User management
  async signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    return { user: data.user, error };
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { user: data.user, session: data.session, error };
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  async resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    return { data, error };
  },

  // Resend the email-confirmation link for a freshly signed-up user.
  // Throws no errors directly — caller inspects the returned `error`.
  async resendSignupConfirmation(email) {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    return { data, error };
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  async updateUserName(displayName) {
    const { data, error } = await supabase.auth.updateUser({
      data: { display_name: displayName }
    });
    return { user: data.user, error };
  },

  async getUserName() {
    const { user } = await this.getCurrentUser();
    return user?.user_metadata?.display_name || null;
  },

  // Project management
  async createProject(projectData) {
    const { data, error } = await supabase
      .from('projects')
      .insert([projectData])
      .select()
      .single();
    return { data, error };
  },

  async getProjects(userId) {
    const { data, error } = await supabase
      .from('projects')
      .select('id, name, client_name, status, last_analyzed, slide_count, overall_score, thumbnail_url')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(200);
    if (error || !data) return { data, error };
    // Append projects shared with this user (project_members). Tolerates the
    // table not existing yet (migration not run) — owned projects still load.
    try {
      const { data: memberships } = await supabase
        .from('project_members')
        .select('project_id, role')
        .eq('user_id', userId);
      if (memberships?.length) {
        const roleByProject = {};
        memberships.forEach(m => { roleByProject[m.project_id] = m.role || 'viewer'; });
        const ownedIds = new Set(data.map(p => p.id));
        const sharedIds = Object.keys(roleByProject).filter(id => !ownedIds.has(id));
        if (sharedIds.length) {
          const { data: shared } = await supabase
            .from('projects')
            .select('id, name, client_name, status, last_analyzed, slide_count, overall_score, thumbnail_url')
            .in('id', sharedIds)
            .order('created_at', { ascending: false });
          (shared || []).forEach(p => {
            data.push({ ...p, is_shared: true, shared_role: roleByProject[p.id] });
          });
        }
      }
    } catch (_) { /* project_members may not exist yet */ }
    return { data, error };
  },

  async getProjectFull(projectId) {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();
    return { data, error };
  },

  async updateProject(projectId, updates) {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single();
    return { data, error };
  },

  async deleteProject(projectId) {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);
    return { error };
  },

  // File storage
  async uploadFile(bucket, path, file) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      });
    return { data, error };
  },

  async downloadFile(bucket, path) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path);
    return { data, error };
  },

  async deleteFile(bucket, path) {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);
    return { error };
  },

  async getFileUrl(bucket, path) {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    return data.publicUrl;
  },

  getPublicUrl(bucket, path) {
    return supabase.storage
      .from(bucket)
      .getPublicUrl(path);
  },

  // Analysis results
  async saveAnalysisResult(analysisData) {
    const { data, error } = await supabase
      .from('analysis_results')
      .insert([analysisData])
      .select()
      .single();
    return { data, error };
  },

  async getAnalysisResults(projectId) {
    const { data, error } = await supabase
      .from('analysis_results')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Knowledge base
  async addKnowledgeItem(itemData) {
    const { data, error } = await supabase
      .from('knowledge_base')
      .insert([itemData])
      .select()
      .single();
    return { data, error };
  },

  async getKnowledgeItems(category = null) {
    let query = supabase
      .from('knowledge_base')
      .select('*')
      .order('created_at', { ascending: false });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;
    return { data, error };
  },

  // PowerPoint Redesign management
  async saveRedesignData(projectId, redesignData) {
    const { data, error } = await supabase
      .from('projects')
      .update({ redesign_data: redesignData })
      .eq('id', projectId)
      .select()
      .single();
    return { data, error };
  },

  async getRedesignData(projectId) {
    const { data, error } = await supabase
      .from('projects')
      .select('redesign_data')
      .eq('id', projectId)
      .single();
    return { data: data?.redesign_data, error };
  },

  // Brand Guidelines management
  async saveBrandGuidelines(userId, guidelines) {
    // First check if user already has brand guidelines
    const { data: existing } = await supabase
      .from('brand_guidelines')
      .select('id')
      .eq('user_id', userId)
      .single();

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('brand_guidelines')
        .update({
          guidelines_data: guidelines,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();
      return { data, error };
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('brand_guidelines')
        .insert([{
          user_id: userId,
          guidelines_data: guidelines
        }])
        .select()
        .single();
      return { data, error };
    }
  },

  async getBrandGuidelines(userId) {
    const { data, error } = await supabase
      .from('brand_guidelines')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data: data?.guidelines_data, error };
  },

  async uploadBrandAsset(userId, file, assetType) {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${assetType}_${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('brand-assets')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) return { data: null, error };

    const { data: urlData } = supabase.storage
      .from('brand-assets')
      .getPublicUrl(fileName);

    return { data: { path: fileName, url: urlData.publicUrl }, error: null };
  },

  // Helper to convert base64 to Blob
  base64ToBlob(base64, mimeType = 'image/png') {
    // Strip any duplicated data URL prefixes ("data:...;base64,data:...;base64,XXX")
    // before extracting the payload — keeps uploads working even if upstream code
    // accidentally double-prefixed the URL.
    const normalized = normalizeImageUrl(base64);
    const base64Data = normalized.includes(',') ? normalized.split(',').pop() : normalized;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  },

  // Upload slide image to storage and return URL
  async uploadSlideImage(userId, projectId, slideIndex, base64Image) {
    if (!base64Image || !base64Image.startsWith('data:')) {
      // Already a URL or invalid, return as-is
      return base64Image;
    }

    try {
      // Normalize first so duplicated prefixes don't break the upload
      const normalized = normalizeImageUrl(base64Image);
      const blob = this.base64ToBlob(normalized);
      const fileName = `${userId}/${projectId}/slide_${slideIndex}_${Date.now()}.png`;

      const { data, error } = await supabase.storage
        .from('ppt-slides')
        .upload(fileName, blob, {
          cacheControl: '31536000', // 1 year cache
          upsert: true,
          contentType: 'image/png'
        });

      if (error) {
        console.error('Slide image upload error:', error);
        return base64Image; // Fallback to base64 if upload fails
      }

      const { data: urlData } = supabase.storage
        .from('ppt-slides')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (err) {
      console.error('Slide image upload exception:', err);
      return base64Image; // Fallback to base64
    }
  },

  // Upload base64 imagery to storage + strip heavy fields so a deck's slides
  // are DB-safe. Shared by savePPTProject and the versioned /save path.
  async processSlidesForSave(userId, projectId, slides) {
    console.log(`[SAVE] Uploading ${slides?.length || 0} slide images to storage...`);
    const processedSlides = await Promise.all(
      (slides || []).map(async (slide, index) => {
        // Upload preview_url if it's base64. Normalize first to recover from
        // legacy double-prefixed strings ("data:...;base64,data:...;base64,XXX").
        let processedPreviewUrl = normalizeImageUrl(slide.preview_url);
        if (processedPreviewUrl && processedPreviewUrl.startsWith('data:')) {
          processedPreviewUrl = await this.uploadSlideImage(userId, projectId, index, processedPreviewUrl);
        }

        // Upload _slideImageUrl (AI-generated photograph) if it's base64
        let processedSlideImageUrl = normalizeImageUrl(slide._slideImageUrl || null);
        if (processedSlideImageUrl && processedSlideImageUrl.startsWith('data:')) {
          processedSlideImageUrl = await this.uploadSlideImage(userId, projectId, `${index}_photo`, processedSlideImageUrl);
        }

        // Return slide with URL instead of base64, stripping large data
        const cleanSlide = {
          ...slide,
          preview_url: processedPreviewUrl,
          _slideImageUrl: processedSlideImageUrl,
        };

        // Strip extractedLayers to avoid huge payloads (contains base64 images)
        // Keep only essential metadata, not the actual layer images
        if (slide.extractedLayers) {
          cleanSlide.extractedLayers = {
            hasLayers: true,
            layerCount: slide.extractedLayers.layers?.length || 0,
            source: slide.extractedLayers.source || null,
            // Don't save the actual layers array with base64 images
          };
        }

        // Also strip any other base64 data that might be lurking
        if (cleanSlide.layout_json?.elements) {
          cleanSlide.layout_json = {
            ...cleanSlide.layout_json,
            elements: cleanSlide.layout_json.elements.map(el => ({
              ...el,
              // Strip any base64 image data from elements
              image: el.image?.startsWith?.('data:') ? '[image-data-stripped]' : el.image
            }))
          };
        }

        return cleanSlide;
      })
    );
    console.log('[SAVE] Image upload complete.');
    return processedSlides;
  },

  // PPT Projects management
  async savePPTProject(userId, projectData) {
    const { title, slides, brandGuidelines, metadata } = projectData;

    // Generate a unique project ID if not provided
    const projectId = projectData.id || `ppt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const processedSlides = await this.processSlidesForSave(userId, projectId, slides);

    // Check if project already exists (update) or is new (insert)
    // Use maybeSingle() to avoid 406 error when no rows match (new project)
    const { data: existing } = await supabase
      .from('ppt_projects')
      .select('id')
      .eq('project_id', projectId)
      .maybeSingle();

    const pptData = {
      project_id: projectId,
      user_id: userId,
      title: title,
      slides_data: processedSlides, // Use processed slides with storage URLs instead of base64
      brand_guidelines: brandGuidelines || null,
      slide_count: processedSlides?.length || 0,
      metadata: metadata || {},
      updated_at: new Date().toISOString()
    };

    if (existing) {
      // Update existing project
      const { data, error } = await supabase
        .from('ppt_projects')
        .update(pptData)
        .eq('project_id', projectId)
        .select()
        .single();
      return { data, error, isNew: false };
    } else {
      // Insert new project
      pptData.created_at = new Date().toISOString();
      const { data, error } = await supabase
        .from('ppt_projects')
        .insert([pptData])
        .select()
        .single();
      return { data, error, isNew: true };
    }
  },

  async getPPTProjects(userId) {
    const { data, error } = await supabase
      .from('ppt_projects')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    return { data, error };
  },

  async getPPTProject(projectId) {
    const { data, error } = await supabase
      .from('ppt_projects')
      .select('*')
      .eq('project_id', projectId)
      .single();
    return { data, error };
  },

  async deletePPTProject(projectId, userId) {
    const { error } = await supabase
      .from('ppt_projects')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);
    return { error };
  },

  async duplicatePPTProject(projectId, userId) {
    // Get the original project
    const { data: original, error: fetchError } = await this.getPPTProject(projectId);
    if (fetchError || !original) return { data: null, error: fetchError || new Error('Project not found') };

    // Create a copy with new ID
    const newProjectData = {
      title: `${original.title} (Copy)`,
      slides: original.slides_data,
      brandGuidelines: original.brand_guidelines,
      metadata: { ...original.metadata, copied_from: projectId }
    };

    return await this.savePPTProject(userId, newProjectData);
  }
};

// Real-time subscriptions
export const subscribeToProjects = (userId, callback) => {
  return supabase
    .channel('projects')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'projects',
        filter: `user_id=eq.${userId}`
      },
      callback
    )
    .subscribe();
};

export const subscribeToAnalysisResults = (projectId, callback) => {
  return supabase
    .channel('analysis_results')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'analysis_results',
        filter: `project_id=eq.${projectId}`
      },
      callback
    )
    .subscribe();
};

export default supabase;